export type InstrumentId = 'acoustic-grand' | 'upright-piano' | 'electric-piano' | 'synth-piano';

export interface InstrumentOption {
  id: InstrumentId;
  name: string;
  category: 'acoustic' | 'electric' | 'modeled';
  description: string;
  badge: string;
}

export interface InstrumentStatus {
  id: InstrumentId;
  state: 'idle' | 'loading' | 'ready' | 'error';
  progress?: number; // 0 to 100
  error?: string;
}

export interface VirtualInstrument {
  readonly id: InstrumentId;
  readonly isReady: boolean;
  load(): Promise<void>;
  playNoteOn(pitch: number, velocity?: number, time?: number): (stopTime?: number) => void;
  scheduleNote(pitch: number, velocity: number, startTime: number, durationSeconds: number): void;
  setDecayTime?(decaySeconds: number): void;
  stopAll(): void;
}

export interface ReverbSettings {
  enabled: boolean;
  mix: number; // 0 to 100%
  roomSize: number; // 0.6s to 3.5s
  damping: 'warm' | 'natural' | 'bright';
}

export interface ToneSettings {
  enabled: boolean;
  brightness: number; // -50 (feutré/mellow) à +50 (brillant/bright)
}

export interface DelaySettings {
  enabled: boolean;
  time: number; // 0.05s to 1.0s
  feedback: number; // 0 to 80%
  mix: number; // 0 to 100%
}

export interface PianoEffectsSettings {
  reverb: ReverbSettings;
  delay: DelaySettings;
  tone: ToneSettings;
  decayTime: number; // 0.2s to 3.0s
}

export const INSTRUMENT_OPTIONS: InstrumentOption[] = [
  {
    id: 'acoustic-grand',
    name: 'Steinway Concert Grand',
    category: 'acoustic',
    description: 'Sampled 9ft Steinway concert grand with 5 velocity layers, natural decay and hall resonance',
    badge: 'Concert Grand',
  },
  {
    id: 'upright-piano',
    name: 'Yamaha Studio Upright',
    category: 'acoustic',
    description: 'Intimate felted acoustic upright with warm wooden body resonance (Versilian VCSL)',
    badge: 'Studio Upright',
  },
  {
    id: 'electric-piano',
    name: 'Wurlitzer 200A EP',
    category: 'electric',
    description: 'Authentic vintage electro-acoustic reed piano samples with warm tube drive feel',
    badge: 'Vintage EP',
  },
  {
    id: 'synth-piano',
    name: 'Rhodes FM Tines (Modeled)',
    category: 'modeled',
    description: 'Zero-download FM physical modeling with crystalline hammer bell attack and warm body',
    badge: 'Modeled FM',
  },
];
